/**
 * TEMP-H-006: Loading Skeletons & States
 * 
 * Accessible loading skeletons and states with proper ARIA attributes
 * and performance optimizations.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/utils/cn';

// =============================================================================
// BASE SKELETON COMPONENTS
// =============================================================================

interface SkeletonProps {
  className?: string;
  animate?: boolean;
  'aria-label'?: string;
  style?: React.CSSProperties;
}

const Skeleton: React.FC<SkeletonProps> = ({ 
  className, 
  animate = true, 
  'aria-label': ariaLabel,
  style 
}) => (
  <div
    className={cn(className)}
    style={{
      background: `linear-gradient(90deg, var(--bg-subtle) 25%, var(--bg-elevated) 50%, var(--bg-subtle) 75%)`,
      backgroundSize: '200% 100%',
      borderRadius: 'var(--radius-md)',
      ...(animate && {
        animation: `skeleton-loading var(--motion-duration-slower) infinite`
      }),
      ...style
    }}
    role="status"
    aria-label={ariaLabel || 'Loading'}
  >
    <span className="sr-only">Loading...</span>
  </div>
);

const ShimmerSkeleton: React.FC<SkeletonProps> = ({ className, 'aria-label': ariaLabel }) => (
  <div
    className={cn('relative overflow-hidden', className)}
    style={{
      backgroundColor: 'var(--bg-subtle)',
      borderRadius: 'var(--radius-md)'
    }}
    role="status"
    aria-label={ariaLabel || 'Loading'}
  >
    <motion.div
      className="absolute inset-0"
      style={{
        background: `linear-gradient(90deg, transparent, var(--fg-muted), transparent)`,
        opacity: 0.3
      }}
      animate={{
        x: ['-100%', '100%']
      }}
      transition={{
        repeat: Infinity,
        duration: 1.5,
        ease: 'linear'
      }}
    />
    <span className="sr-only">Loading...</span>
  </div>
);

// =============================================================================
// SPECIALIZED LOADING STATES
// =============================================================================

interface LoadingStateProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showSpinner?: boolean;
}

export const LoadingSpinner: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  size = 'md',
  className,
  showSpinner = true
}) => {
  const sizeStyles = {
    sm: { width: 'var(--space-5)', height: 'var(--space-5)' },
    md: { width: 'var(--space-7)', height: 'var(--space-7)' }, 
    lg: { width: 'var(--space-9)', height: 'var(--space-9)' }
  };

  return (
    <div 
      className={cn('flex items-center justify-center', className)}
      style={{ gap: 'var(--space-4)' }}
      role="status"
      aria-live="polite"
    >
      {showSpinner && (
        <Loader2 
          className="animate-spin"
          style={{ 
            ...sizeStyles[size],
            color: 'var(--accent)'
          }}
          aria-hidden="true"
        />
      )}
      <span style={{ color: 'var(--fg-muted)' }}>{message}</span>
      <span className="sr-only">Loading, please wait</span>
    </div>
  );
};

export const RefreshingIndicator: React.FC<{ message?: string; className?: string }> = ({
  message = 'Refreshing...',
  className
}) => (
  <div 
    className={cn('flex items-center', className)}
    style={{ 
      gap: 'var(--space-2)', 
      fontSize: 'var(--font-size-sm)', 
      color: 'var(--fg-muted)' 
    }}
    role="status"
    aria-live="polite"
  >
    <RefreshCw 
      className="animate-spin" 
      style={{ 
        width: 'var(--space-5)', 
        height: 'var(--space-5)' 
      }}
      aria-hidden="true" 
    />
    <span>{message}</span>
  </div>
);

// =============================================================================
// COMPLEX SKELETON LAYOUTS
// =============================================================================

export const IterationListSkeleton: React.FC<{ 
  count?: number; 
  compact?: boolean;
  className?: string;
}> = ({ 
  count = 5, 
  compact = false,
  className 
}) => (
  <div 
    className={cn(className)}
    style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-5)'
    }}
    role="status"
    aria-label={`Loading ${count} iterations`}
  >
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        style={{
          backgroundColor: 'var(--bg-surface)',
          border: `1px solid var(--border-subtle)`,
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-5)'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between"
          style={{ marginBottom: 'var(--space-4)' }}
        >
          <div 
            className="flex items-center"
            style={{ gap: 'var(--space-2)' }}
          >
            <Skeleton className="h-5 w-20" aria-label="Iteration number" />
            <Skeleton className="h-5 w-16" aria-label="Status badge" />
          </div>
          <div 
            className="flex items-center"
            style={{ gap: 'var(--space-4)' }}
          >
            <Skeleton className="h-4 w-12" aria-label="Timestamp" />
            <Skeleton className="h-4 w-10" aria-label="Cost" />
          </div>
        </div>
        
        {/* Model info */}
        {!compact && (
          <Skeleton className="h-3 w-32 mb-3" aria-label="Model information" />
        )}
        
        {/* Artifacts */}
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)'
        }}>
          <Skeleton className="h-4 w-16" aria-label="Artifacts header" />
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)'
          }}>
            <Skeleton className="h-4 w-48" aria-label="Artifact link" />
            <Skeleton className="h-4 w-40" aria-label="Artifact link" />
            {!compact && <Skeleton className="h-4 w-36" aria-label="Artifact link" />}
          </div>
        </div>
      </div>
    ))}
    <span className="sr-only">Loading iteration data</span>
  </div>
);

export const TimelineEventSkeleton: React.FC<{ 
  count?: number;
  className?: string;
}> = ({ 
  count = 8,
  className 
}) => (
  <div 
    className={cn(className)}
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-4)'
    }}
    role="status"
    aria-label={`Loading ${count} timeline events`}
  >
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        className="flex items-start"
        style={{
          gap: 'var(--space-5)',
          padding: 'var(--space-4)',
          backgroundColor: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          border: `1px solid var(--border-subtle)`
        }}
      >
        {/* Icon */}
        <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" aria-label="Event icon" />
        
        {/* Content */}
        <div 
          className="flex-1"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)'
          }}
        >
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-32" aria-label="Event title" />
            <Skeleton className="h-3 w-16" aria-label="Timestamp" />
          </div>
          <Skeleton className="h-3 w-full" aria-label="Event description" />
          <Skeleton className="h-3 w-3/4" aria-label="Event details" />
        </div>
      </div>
    ))}
    <span className="sr-only">Loading timeline events</span>
  </div>
);

export const StageSettingsSkeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div 
    className={cn(className)}
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-7)'
    }}
    role="status"
    aria-label="Loading stage settings"
  >
    {/* Header */}
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-2)'
    }}>
      <Skeleton 
        className="h-6 w-48" 
        style={{ height: 'var(--space-7)', width: '12rem' }}
        aria-label="Stage title" 
      />
      <Skeleton 
        className="h-4 w-64" 
        style={{ height: 'var(--space-5)', width: '16rem' }}
        aria-label="Stage description" 
      />
    </div>
    
    {/* Settings grid */}
    <div 
      className="grid grid-cols-1 md:grid-cols-2"
      style={{ gap: 'var(--space-7)' }}
    >
      {Array.from({ length: 6 }).map((_, index) => (
        <div 
          key={index} 
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)'
          }}
        >
          <Skeleton 
            style={{ height: 'var(--space-5)', width: '6rem' }}
            aria-label="Setting label" 
          />
          <Skeleton 
            style={{ height: 'var(--space-10)', width: '100%' }}
            aria-label="Setting input" 
          />
        </div>
      ))}
    </div>
    
    {/* Actions */}
    <div 
      className="flex items-center"
      style={{ gap: 'var(--space-4)' }}
    >
      <Skeleton 
        style={{ height: 'var(--space-10)', width: '5rem' }}
        aria-label="Save button" 
      />
      <Skeleton 
        style={{ height: 'var(--space-10)', width: '5rem' }}
        aria-label="Reset button" 
      />
    </div>
    
    <span className="sr-only">Loading stage configuration</span>
  </div>
);

export const ExecutionOverviewSkeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div 
    className={cn(className)}
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-7)'
    }}
    role="status"
    aria-label="Loading execution overview"
  >
    {/* Header */}
    <div className="flex items-center justify-between">
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)'
      }}>
        <Skeleton className="h-8 w-64" aria-label="Execution title" />
        <Skeleton className="h-4 w-32" aria-label="Execution ID" />
      </div>
      <div 
        className="flex items-center"
        style={{ gap: 'var(--space-4)' }}
      >
        <Skeleton className="h-6 w-16" aria-label="Status badge" />
        <Skeleton className="h-10 w-24" aria-label="Action button" />
      </div>
    </div>
    
    {/* Stats grid */}
    <div 
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
      style={{ gap: 'var(--space-5)' }}
    >
      {Array.from({ length: 4 }).map((_, index) => (
        <div 
          key={index} 
          style={{
            backgroundColor: 'var(--bg-surface)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)'
          }}
        >
          <Skeleton className="h-4 w-16" aria-label="Metric label" />
          <Skeleton className="h-8 w-12" aria-label="Metric value" />
        </div>
      ))}
    </div>
    
    {/* Stage pipeline */}
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-5)'
    }}>
      <Skeleton className="h-6 w-32" aria-label="Pipeline title" />
      <div 
        className="flex items-center overflow-x-auto"
        style={{ gap: 'var(--space-2)' }}
      >
        {Array.from({ length: 8 }).map((_, index) => (
          <div 
            key={index} 
            className="flex items-center flex-shrink-0"
            style={{ gap: 'var(--space-2)' }}
          >
            <Skeleton className="w-10 h-10 rounded-full" aria-label="Stage icon" />
            <Skeleton className="h-4 w-16" aria-label="Stage name" />
            {index < 7 && <Skeleton className="h-px w-8" aria-label="Stage connector" />}
          </div>
        ))}
      </div>
    </div>
    
    <span className="sr-only">Loading execution overview data</span>
  </div>
);

// =============================================================================
// PROGRESSIVE LOADING STATES
// =============================================================================

interface ProgressiveLoadingProps {
  stage: 'initial' | 'loading' | 'error' | 'success';
  children?: React.ReactNode;
  error?: string;
  onRetry?: () => void;
  loadingComponent?: React.ReactNode;
  className?: string;
}

export const ProgressiveLoading: React.FC<ProgressiveLoadingProps> = ({
  stage,
  children,
  error,
  onRetry,
  loadingComponent,
  className
}) => {
  switch (stage) {
    case 'initial':
      return (
        <div 
          className={cn('flex items-center justify-center', className)}
          style={{ padding: 'var(--space-9)' }}
        >
          <div className="text-center">
            <div 
              className="mx-auto mb-4 flex items-center justify-center"
              style={{
                width: 'var(--space-12)',
                height: 'var(--space-12)',
                backgroundColor: 'var(--bg-subtle)',
                borderRadius: 'var(--radius-full)',
                marginBottom: 'var(--space-5)'
              }}
            >
              <div style={{
                width: 'var(--space-7)',
                height: 'var(--space-7)',
                backgroundColor: 'var(--fg-disabled)',
                borderRadius: 'var(--radius-full)'
              }} />
            </div>
            <p style={{ color: 'var(--fg-muted)' }}>Ready to load</p>
          </div>
        </div>
      );
      
    case 'loading':
      return (
        <div className={cn('animate-fade-in', className)}>
          {loadingComponent || <LoadingSpinner />}
        </div>
      );
      
    case 'error':
      return (
        <div 
          className={cn('flex items-center justify-center', className)}
          style={{ padding: 'var(--space-9)' }}
        >
          <div className="text-center">
            <div 
              className="mx-auto mb-4 flex items-center justify-center"
              style={{
                width: 'var(--space-12)',
                height: 'var(--space-12)',
                backgroundColor: 'var(--danger-bg-subtle)',
                borderRadius: 'var(--radius-full)',
                marginBottom: 'var(--space-5)'
              }}
            >
              <div style={{
                width: 'var(--space-7)',
                height: 'var(--space-7)',
                backgroundColor: 'var(--danger)',
                borderRadius: 'var(--radius-full)'
              }} />
            </div>
            <p 
              style={{ 
                color: 'var(--danger)', 
                marginBottom: 'var(--space-5)' 
              }}
            >
              {error || 'Something went wrong'}
            </p>
            {onRetry && (
              <button
                onClick={onRetry}
                style={{
                  padding: `var(--space-2) var(--space-5)`,
                  backgroundColor: 'var(--danger-bg-subtle)',
                  color: 'var(--danger)',
                  border: `1px solid var(--danger-border-subtle)`,
                  borderRadius: 'var(--radius-lg)',
                  transition: `all var(--motion-duration-fast) var(--motion-ease-standard)`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--danger-bg-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--danger-bg-subtle)';
                }}
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      );
      
    case 'success':
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={className}
        >
          {children}
        </motion.div>
      );
      
    default:
      return null;
  }
};

// =============================================================================
// SKELETON SHIMMER ANIMATION
// =============================================================================

export const SkeletonContainer: React.FC<{
  children: React.ReactNode;
  className?: string;
  animate?: boolean;
}> = ({ 
  children, 
  className,
  animate = true 
}) => (
  <div
    className={cn('relative', className)}
    style={{
      ...(animate && {
        animation: `pulse var(--motion-duration-slow) var(--motion-ease-standard) infinite`
      })
    }}
    role="status"
    aria-live="polite"
  >
    {children}
    <span className="sr-only">Content is loading</span>
  </div>
);

// Re-export skeleton for convenience
export { Skeleton, ShimmerSkeleton };